import MenuIcon from "@mui/icons-material/Menu";
import {
  Button,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import styled from "styled-components";
import Flex from "../../../../../components/Flex";
import Text from "../../../../../components/Text";
import { useQueryParams } from "../../../../../hooks/use-query-param";

interface TabItem {
  id: string;
  label: string;
  children?: TabItem[];
}

interface PublicAssessmentsSideTabProps {
  activeTab: { ind: number; val: string };
  activeChildTab: { ind: number; val: string };
  handleTabChange: ({
    ind,
    childInd,
  }: {
    ind: number;
    childInd?: number;
  }) => void;
}

const PublicAssessmentsSideTab = ({
  activeTab,
  activeChildTab,
  handleTabChange,
}: PublicAssessmentsSideTabProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const assForms = useQueryParams("assessments")?.split(",");
  const isAssFormContainNull = assForms?.includes("null") || !assForms?.length;

  const intakeForms = useQueryParams("intakeform")?.split(",");
  const isIntakeFormContainNull =
    intakeForms?.includes("null") || !intakeForms?.length;

  const screeningTab = {
    id: "screening",
    label: "Screening Forms",
    children: assForms?.map((it) => ({ id: it, label: it })),
  };

  const tabStructure: TabItem[] =
    !isIntakeFormContainNull && !!intakeForms?.length
      ? [
          {
            id: "intakeForm",
            label: "Intake Forms",
          },
          ...(!isAssFormContainNull ? [screeningTab] : []),
        ]
      : !isAssFormContainNull && !!assForms?.length
        ? [screeningTab]
        : [];

  const handleTabClick = (index: number, childInd?: number) => {
    handleTabChange({ ind: index, childInd });
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const renderTabsContent = () => (
    <Flex flexDirection={"column"} gap={1} height={"100%"}>
      <Tabs
        orientation="vertical"
        value={tabStructure.findIndex(
          (tab) =>
            tab.id === activeTab.val ||
            tab.children?.some((child) => child.id === activeTab.val)
        )}
        variant="standard"
        indicatorColor={"white" as any}
        style={{
          height: "100%",
        }}
      >
        {tabStructure.map((tab, index) => (
          <Flex
            position={"relative"}
            key={tab.id}
            style={{
              minWidth: 200,
            }}
          >
            <Tab
              style={{
                minWidth: 200,
              }}
              label={
                <Button
                  variant={tab.id === activeTab.val ? "contained" : "outlined"}
                  fullWidth
                  onClick={() => handleTabClick(index)}
                >
                  <Text
                    fontWeight={tab.id === activeTab.val ? "bold" : undefined}
                    color={tab.id === activeTab.val ? "white" : undefined}
                  >
                    {index + 1}. &nbsp; {tab.label}
                  </Text>
                </Button>
              }
            />

            <Flex
              flexDirection="column"
              key={tab.id}
              position={"absolute"}
              top={55}
              style={{ overflow: "auto" }}
              width={"100%"}
            >
              <Flex
                flexDirection={"column"}
                gap={1}
                height={"100%"}
                maxHeight={"calc(100vh - 40px)"}
                mb={2}
                width={"100%"}
                overflow={"auto"}
              >
                {tab.children &&
                  tab.id === activeTab.val &&
                  tab.children.map((child, childIndex) => (
                    <StyledChildTab
                      key={child.id}
                      label={
                        <Flex
                          justifyContent={"left"}
                          alignItems="center"
                          gap={1}
                          width={"100%"}
                          onClick={() => handleTabClick(index, childIndex)}
                        >
                          <Text
                            color={
                              childIndex === activeChildTab.ind
                                ? "primary.main"
                                : undefined
                            }
                            fontWeight={
                              childIndex === activeChildTab.ind
                                ? "bold"
                                : undefined
                            }
                          >
                            {index + 1}.{childIndex + 1} &nbsp; {child.label}
                          </Text>
                        </Flex>
                      }
                    />
                  ))}
              </Flex>
            </Flex>
          </Flex>
        ))}
      </Tabs>
    </Flex>
  );

  if (isMobile) {
    const activeTabItem = tabStructure.find(
      (tab) =>
        tab.id === activeTab.val ||
        tab.children?.some((child) => child.id === activeTab.val)
    );
    const activeChildTabItem = activeTabItem?.children?.find(
      (child) => child.id === activeChildTab.val
    );

    const displayLabel = activeChildTabItem
      ? `${activeTabItem?.label} - ${activeChildTabItem.label}`
      : activeTabItem?.label || "";

    return (
      <>
        <Flex
          justifyContent="flex-start"
          alignItems="center"
          gap={1}
          width="100%"
        >
          <IconButton
            onClick={() => setDrawerOpen(true)}
            sx={{ color: "primary.main" }}
          >
            <MenuIcon />
          </IconButton>
          <Text fontWeight="medium">{displayLabel}</Text>
        </Flex>
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { width: 280, padding: 2 },
          }}
        >
          {renderTabsContent()}
        </Drawer>
      </>
    );
  }

  return renderTabsContent();
};

export default PublicAssessmentsSideTab;

// const StyledTab = styled(Tab)(() => ({
//   alignItems: "flex-start",
//   textTransform: "none",
//   "&.Mui-selected": {
//     backgroundColor: "#E5F6FF !important",
//   },
// }));

const StyledChildTab = styled(Tab)(() => ({
  alignItems: "flex-start",
  textTransform: "none",
  paddingLeft: "32px !important",
  minHeight: "40px",
  "&.Mui-selected": {
    backgroundColor: "#E5F6FF !important",
  },
}));
